// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// 07 §7's mixing rules (M3-29).
//
// `Mixer` says how a bus is ducked; this says **when**, and the split is deliberate — the graph
// is a fixed thing that can be built and asserted without a single sound in it, while the rules
// are a table of game states that changes every time the game does.
//
// | Rule | Behaviour |
// |---|---|
// | Speech ducking | `music` −8 dB, `ambience` −6 dB while speech is active, 40 ms attack, 250 ms release |
// | Jackpot emphasis | `music` −3 dB for 300 ms on a jackpot stab |
// | Multiball | `mechanical` +2 dB, `ambience` −6 dB |
// | Tilt | Everything but the tilt buzzer drops 18 dB |
// | Blackout (BIG BANG) | All buses to silence for 400 ms, then a hard return |
//
// **The rules compose in decibels, and that is the whole implementation.** A callout during
// multiball ducks the music by 8; a jackpot inside the same callout takes it to 11. Written as
// "set the music to −8" they would fight, and the last one to run would win — which is the
// commonest way a mixing layer ends up with a bus stuck quiet after a mode ends. Written as
// contributions that are summed and applied once a frame, there is no order to get wrong and no
// state to leak: the offset is a pure function of what is happening.
//
// The one rule here that is not a duck is multiball's `mechanical` +2 dB, which is why the
// mixer's automation gain is an **offset** rather than an attenuation.

import type { BusName, Mixer } from './mixer'
import { BUS_NAMES } from './mixer'

/** What the rules need to know. Every field is a fact the game already holds. */
export interface MixState {
  /** The machine is speaking. Drives the largest duck in the table. */
  readonly speaking: boolean
  readonly multiball: boolean
  readonly tilted: boolean
}

/** 07 §7, as numbers. */
export const SPEECH_DUCK_MUSIC_DB = -8
export const SPEECH_DUCK_AMBIENCE_DB = -6
export const JACKPOT_DUCK_MUSIC_DB = -3
export const JACKPOT_EMPHASIS_SECONDS = 0.3
export const MULTIBALL_MECHANICAL_DB = 2
export const MULTIBALL_AMBIENCE_DB = -6
export const TILT_DUCK_DB = -18
export const BLACKOUT_SECONDS = 0.4

/** 40 ms in, 250 ms out (07 §7). The ear is not symmetrical and neither is the duck. */
export const DUCK_ATTACK = 0.04
export const DUCK_RELEASE = 0.25

/**
 * The bus the tilt buzzer is on, and the one exception to the tilt rule.
 *
 * 07 §7 says "everything but the tilt buzzer drops 18 dB". The buzzer plays on `game`, and
 * during a tilt nothing else does — the game is over for that ball and every scoring rule is
 * dead — so exempting the bus is exempting the buzzer. If a future machine made noise on `game`
 * while tilted, this is the line that would need to become a per-voice exemption.
 */
const TILT_EXEMPT: BusName = 'game'

export class MixRules {
  private readonly mixer: Mixer
  /** Context time the jackpot emphasis and the blackout expire at. */
  private emphasisUntil = 0
  private blackoutUntil = 0
  /** What was last written to each bus, so an unchanged frame writes nothing. */
  private applied = new Map<BusName, number>()
  private silenced = false

  constructor(mixer: Mixer) {
    this.mixer = mixer
  }

  /** A jackpot stab: `music` −3 dB for 300 ms (07 §7). */
  emphasise(now: number, seconds = JACKPOT_EMPHASIS_SECONDS): void {
    this.emphasisUntil = Math.max(this.emphasisUntil, now + seconds)
  }

  /** BIG BANG: every bus to silence, then a hard return (07 §7). */
  blackout(now: number, seconds = BLACKOUT_SECONDS): void {
    this.blackoutUntil = Math.max(this.blackoutUntil, now + seconds)
  }

  get blackedOut(): boolean {
    return this.silenced
  }

  /** The offset each bus is currently carrying, dB. Reported, so a stuck duck is visible. */
  offsets(): Record<string, number> {
    const out: Record<string, number> = {}
    for (const [bus, db] of this.applied) if (db !== 0) out[bus] = db
    return out
  }

  /**
   * One frame.
   *
   * Idempotent: called with the same state twice it writes nothing the second time, which
   * matters because `setTargetAtTime` re-aimed every frame never actually arrives — it restarts
   * its approach from wherever it had got to, and a duck written sixty times a second converges
   * about as fast as one written once.
   */
  apply(now: number, state: MixState): void {
    if (now < this.blackoutUntil) {
      if (!this.silenced) {
        this.silenced = true
        for (const bus of BUS_NAMES) this.mixer.bus(bus).silence(now)
        this.applied.clear()
      }
      return
    }
    if (this.silenced) {
      // "Then a hard return." Not a fade — the return *is* the effect, and a ramp back would
      // make BIG BANG sound like a fault rather than like a detonation.
      this.silenced = false
      for (const bus of BUS_NAMES) this.mixer.bus(bus).offset(0, 0.001, now)
      for (const bus of BUS_NAMES) this.applied.set(bus, 0)
    }

    const emphasising = now < this.emphasisUntil
    for (const bus of BUS_NAMES) {
      const want = this.offsetFor(bus, state, emphasising)
      if (this.applied.get(bus) === want) continue
      const rising = want > (this.applied.get(bus) ?? 0)
      this.applied.set(bus, want)
      // Fast down, slow up: a duck has to arrive before the first syllable and leave without
      // the music lurching back in.
      this.mixer.bus(bus).offset(want, rising ? DUCK_RELEASE : DUCK_ATTACK, now)
    }
  }

  /** Every rule that touches a bus, summed. */
  private offsetFor(bus: BusName, state: MixState, emphasising: boolean): number {
    let db = 0
    if (state.speaking) {
      if (bus === 'music') db += SPEECH_DUCK_MUSIC_DB
      if (bus === 'ambience') db += SPEECH_DUCK_AMBIENCE_DB
    }
    if (emphasising && bus === 'music') db += JACKPOT_DUCK_MUSIC_DB
    if (state.multiball) {
      if (bus === 'mechanical') db += MULTIBALL_MECHANICAL_DB
      if (bus === 'ambience') db += MULTIBALL_AMBIENCE_DB
    }
    if (state.tilted && bus !== TILT_EXEMPT) db += TILT_DUCK_DB
    return db
  }
}
